import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart, Sprout } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function VolunteerRegister() {
  const search = useQuery();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const initialCode = (search.get("code") || "").toUpperCase();
  const [code, setCode] = useState(initialCode);
  const [validating, setValidating] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const lookup = async (c) => {
    if (!c) { setInviteInfo(null); setInviteError(null); return; }
    setValidating(true);
    try {
      const { data } = await api.post("/volunteer/invite/lookup", { code: c });
      if (data.valid) {
        setInviteInfo(data);
        setInviteError(null);
      } else {
        setInviteInfo(null);
        setInviteError(data.error);
      }
    } catch (err) {
      setInviteInfo(null);
      setInviteError("We couldn't check that code right now. Please try again.");
    } finally { setValidating(false); }
  };

  useEffect(() => {
    if (initialCode) lookup(initialCode);
    // eslint-disable-next-line
  }, [initialCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      toast.error("Passwords don't match yet.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/volunteer/register", {
        code, name, email, password,
      });
      // Persist session so AuthProvider bootstraps properly.
      localStorage.setItem("mypatria.session", JSON.stringify({
        access_token: data.access_token, expires_in: data.expires_in,
      }));
      setUser(data.user);
      toast.success(`Welcome home, ${data.user.name.split(" ")[0]}.`);
      navigate("/volunteer", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const readyToSubmit = inviteInfo && name.trim() && email.trim() && password.length >= 8;

  return (
    <div className="min-h-screen flex items-stretch bg-sage-200" data-testid="volunteer-register-page">
      <aside className="hidden md:flex md:w-2/5 items-center justify-center bg-forest-900 text-cream-100 p-12">
        <div className="max-w-md space-y-6">
          <p className="text-xs uppercase tracking-widest text-terracotta-200 font-medium">
            Patria Church · Volunteers
          </p>
          <h2 className="font-serif text-5xl leading-tight">You Are Seen.<br/>You Are Wanted.<br/><em className="text-terracotta-300">Welcome Home.</em></h2>
          <p className="text-cream-200/90 font-serif italic text-lg">
            "Whatever you do, work heartily, as for the Lord."
          </p>
          <p className="text-sm text-cream-200/70">— Colossians 3:23</p>
        </div>
      </aside>

      <main className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="patria-card w-full max-w-lg p-8">
          <div className="flex items-center gap-2">
            <Sprout className="h-5 w-5 text-terracotta-500" strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
              Volunteer Registration
            </p>
          </div>
          <h1 className="mt-2 text-3xl font-serif text-forest-900">Step Into Serving</h1>
          <p className="mt-2 text-sm text-charcoal-soft">
            Use the invite code your ministry lead shared with you. It's short and case-insensitive.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="patria-label">Invite Code *</label>
              <input
                className="patria-input w-full font-mono tracking-widest uppercase"
                value={code}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase();
                  setCode(v);
                  setInviteInfo(null);
                }}
                onBlur={() => lookup(code)}
                placeholder="PATRIA-VOL-XXXX"
                data-testid="vr-code"
                required
              />
              {validating && <p className="text-xs italic text-charcoal-muted mt-1">Checking your code…</p>}
              {inviteInfo && (
                <p className="text-xs text-forest-700 mt-1" data-testid="vr-invite-valid">
                  <Heart className="inline h-3 w-3 mr-1" strokeWidth={1.5} />
                  Welcome — this invite is for {inviteInfo.campus_name || "your campus"}.
                  {inviteInfo.note ? ` "${inviteInfo.note}"` : ""}
                </p>
              )}
              {inviteError && (
                <p className="text-xs text-terracotta-700 mt-1" data-testid="vr-invite-error">{inviteError}</p>
              )}
            </div>

            <div>
              <label className="patria-label">Your Full Name *</label>
              <input
                className="patria-input w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="vr-name"
                required
              />
            </div>
            <div>
              <label className="patria-label">Email *</label>
              <input
                type="email"
                className="patria-input w-full"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="vr-email"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="patria-label">Create Password *</label>
                <input
                  type="password"
                  className="patria-input w-full"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="vr-password"
                  required
                />
              </div>
              <div>
                <label className="patria-label">Confirm *</label>
                <input
                  type="password"
                  className="patria-input w-full"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  data-testid="vr-password-confirm"
                  required
                />
              </div>
            </div>
            <p className="text-xs italic text-charcoal-muted">
              Passwords need at least 8 characters, one uppercase, one lowercase, and one number.
            </p>

            <button
              type="submit"
              disabled={!readyToSubmit || submitting}
              className="patria-btn-primary w-full"
              data-testid="vr-submit"
            >
              {submitting ? "Creating your account…" : "Create My Volunteer Account"}
            </button>

            <p className="text-xs text-center text-charcoal-muted mt-4">
              Already have an account?{" "}
              <Link to="/login" className="text-forest-700 hover:text-forest-900 underline">Sign in</Link>.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
